"use client"

import { useState, useEffect } from "react"
import { useRouter, usePathname, useSearchParams } from "next/navigation"
import Link from "next/link"
import Image from "next/image"
import { MessageSquare, History, LogOut, LogIn, Lock, X } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { useControlBar } from "@/components/control-bar"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { replaceQuestionVariables } from "@/lib/question-variables"

const typeColors = {
  Personal: "var(--chart-1)",
  Behavioral: "var(--chart-6)",
  Reflective: "var(--chart-7)",
  Situational: "var(--chart-2)",
  Ethics: "var(--primary)",
  Policy: "var(--chart-4)",
  Roleplay: "var(--chart-3)",
}

const countryFlags = {
  Canada: "\u{1F1E8}\u{1F1E6}",
  Australia: "\u{1F1E6}\u{1F1FA}",
  UK: "\u{1F1EC}\u{1F1E7}",
  USA: "\u{1F1FA}\u{1F1F8}",
}

const programIcons = {
  Medicine: "\u2695\uFE0F",
  Dentistry: "\u{1F9B7}",
  Veterinary: "\u{1F43E}",
  Pharmacy: "\u{1F48A}",
  Optometry: "\u{1F441}\uFE0F",
  Podiatry: "\u{1F9B6}",
}

function formatRelativeDate(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const navItems = [
  { id: "questions", title: "Questions", icon: MessageSquare },
  { id: "history", title: "History", icon: History },
]

export function AppSidebar({ recordings = [], user = null }) {
  const { setOpen, isMobile, setOpenMobile } = useSidebar()
  const { isInterviewActive, busy, accessTier, questions, mode } = useControlBar()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Defer localStorage-dependent UI until after hydration to avoid mismatch
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  const isHistoryRoute = pathname.startsWith("/app/feedback") || pathname.startsWith("/app/history")
  const activeView = isHistoryRoute ? "history" : "questions"

  const pathId = pathname.split("/")[2] ?? null
  // Derive activeId from pathname first (stable across server/client),
  // then sync with searchParams after hydration to avoid mismatch
  const [activeId, setActiveId] = useState(pathId)
  useEffect(() => {
    setActiveId(pathId || searchParams.get("q"))
  }, [pathId, searchParams])
  // Extract recording ID from /app/feedback/[recordingId]
  const activeRecordingId = pathname.startsWith("/app/feedback") ? (pathname.split("/")[3] ?? null) : null

  const [historyFilter, setHistoryFilter] = useState(null)

  const filteredRecordings = historyFilter
    ? recordings.filter((r) => r.question_id === historyFilter)
    : recordings

  const handleQuestionClick = (id) => {
    if (isMobile) setOpenMobile(false)
    if (pathId) {
      router.push(`/app?q=${id}`)
    } else {
      router.push(`/app?q=${id}`, { scroll: false })
    }
  }

  return (
    <Sidebar
      collapsible="icon"
      className={`overflow-hidden ${isInterviewActive || busy ? "pointer-events-none opacity-50" : ""}`}
    >
      <div className="flex h-full w-full flex-row">
      {/* Icon rail */}
      {isMobile ? (
        <div className="flex w-12 shrink-0 flex-col items-center gap-1 border-r py-3">
          <Link href="/" className="flex h-8 w-8 items-center justify-center">
            <Image src="/icon-tile.svg" alt="" width={100} height={100} className="size-7" unoptimized />
          </Link>
          {navItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                if (item.id === "questions" && isHistoryRoute) {
                  setHistoryFilter(null)
                  router.push("/app")
                } else if (item.id === "history") {
                  setHistoryFilter(null)
                  if (!isHistoryRoute) {
                    router.push(recordings.length > 0 ? `/app/feedback/${recordings[0].id}` : "/app/history")
                  }
                }
              }}
              className={`flex h-8 w-8 items-center justify-center rounded-md transition-colors ${
                activeView === item.id
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              }`}
            >
              <item.icon className="h-4 w-4" />
            </button>
          ))}
        </div>
      ) : (
        <Sidebar
          collapsible="none"
          className="w-[calc(var(--sidebar-width-icon)+1px)]! border-r"
        >
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton size="lg" className="md:h-8 md:p-0 justify-center" asChild>
                  <Link href="/">
                    <Image src="/icon-tile.svg" alt="" width={100} height={100} className="size-7" unoptimized />
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent className="px-1.5 md:px-0">
                <SidebarMenu>
                  {navItems.map((item) => (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        tooltip={{ children: item.title, hidden: false }}
                        onClick={() => {
                          setOpen(true)
                          if (item.id === "questions" && isHistoryRoute) {
                            setHistoryFilter(null)
                            router.push("/app")
                          } else if (item.id === "history") {
                            setHistoryFilter(null)
                            if (!isHistoryRoute) {
                              router.push(recordings.length > 0 ? `/app/feedback/${recordings[0].id}` : "/app/history")
                            }
                          }
                        }}
                        isActive={activeView === item.id}
                        className="px-2.5 md:px-2"
                      >
                        <item.icon />
                        <span>{item.title}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
        </Sidebar>
      )}

      {/* Content panel */}
      <Sidebar collapsible="none" className="flex flex-1">
        <SidebarHeader className="gap-3.5 border-b p-3 sm:p-4">

          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-foreground">
              {activeView === "questions" ? "Questions" : "History"}
            </span>
            {activeView === "history" && filteredRecordings.length > 0 && (
              <Badge variant="secondary" className="ml-auto text-xs">
                {filteredRecordings.length}
              </Badge>
            )}
          </div>
          {activeView === "history" && historyFilter && (
            <button
              onClick={() => setHistoryFilter(null)}
              className="flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
            >
              <span className="truncate">
                {replaceQuestionVariables(questions.find((q) => q.id === historyFilter)?.question)?.slice(0, 30) || "Filtered"}
                {(replaceQuestionVariables(questions.find((q) => q.id === historyFilter)?.question)?.length ?? 0) > 30 ? "…" : ""}
              </span>
              <X className="h-3 w-3 shrink-0" />
            </button>
          )}
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup className="px-0">
            <SidebarGroupContent>
              {activeView === "questions" &&
                questions.map((q) => {
                  const isLimited = accessTier?.tier === "limited"
                  const roleplayLocked = mounted && q.type === "Roleplay" && mode === "async"

                  const item = (
                    <button
                      key={q.id}
                      onClick={() => !roleplayLocked && handleQuestionClick(q.id)}
                      className={`flex w-full flex-col items-start gap-2 border-b px-3 py-3 text-left text-sm leading-tight transition-colors last:border-b-0 sm:p-4 ${
                        roleplayLocked
                          ? "opacity-50 cursor-default"
                          : activeId === q.id
                            ? "bg-sidebar-accent text-sidebar-accent-foreground"
                            : "hover:bg-sidebar-accent/50"
                      }`}
                    >
                      <div className="flex w-full items-center gap-2">
                        <span
                          className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium"
                          style={typeColors[q.type] ? { backgroundColor: `color-mix(in oklch, ${typeColors[q.type]} 10%, transparent)`, color: typeColors[q.type] } : undefined}
                        >
                          {q.type}
                        </span>
                        {roleplayLocked && (
                          <Lock className="h-3 w-3 text-muted-foreground" />
                        )}
                        {q.country && countryFlags[q.country] && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-default text-sm">{countryFlags[q.country]}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{q.country}</TooltipContent>
                          </Tooltip>
                        )}
                        {q.program && programIcons[q.program] && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-default text-sm">{programIcons[q.program]}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top">{q.program}</TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                      <span className="line-clamp-2 text-sm">{replaceQuestionVariables(q.question)}</span>
                    </button>
                  )

                  if (roleplayLocked) {
                    return (
                      <Tooltip key={q.id}>
                        <TooltipTrigger asChild>{item}</TooltipTrigger>
                        <TooltipContent side="right">
                          {isLimited ? "Upgrade to unlock Roleplay stations" : "Switch to Live mode for Roleplay stations"}
                        </TooltipContent>
                      </Tooltip>
                    )
                  }

                  return item
                })}

              {activeView === "history" && filteredRecordings.length === 0 && (
                <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
                  <History className="h-8 w-8 text-muted-foreground/40" />
                  <p className="mt-3 text-sm text-muted-foreground">
                    {historyFilter ? "No recordings for this question" : "No history yet"}
                  </p>
                </div>
              )}

              {activeView === "history" &&
                filteredRecordings.map((r) => {
                  const score =
                    r.ai_feedback?.[0]?.feedback_data?.overall_score
                  return (
                    <button
                      key={r.id}
                      onClick={() => {
                        if (isMobile) setOpenMobile(false)
                        router.push(`/app/feedback/${r.id}`)
                      }}
                      className={`flex w-full flex-col items-start gap-2 border-b px-3 py-3 text-left text-sm leading-tight transition-colors last:border-b-0 sm:p-4 ${
                        activeRecordingId === r.id
                          ? "bg-sidebar-accent text-sidebar-accent-foreground"
                          : "hover:bg-sidebar-accent/50"
                      }`}
                    >
                      <div className="flex w-full items-center gap-2">
                        {score != null && (
                          <Badge variant="secondary" className="text-[10px]">
                            {score}/10
                          </Badge>
                        )}
                        <span className="ml-auto text-xs text-muted-foreground">
                          {formatRelativeDate(r.created_at)}
                        </span>
                      </div>
                      <span className="line-clamp-2 text-sm">
                        {r.question_text || "Untitled recording"}
                      </span>
                    </button>
                  )
                })}
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter className="border-t p-3">
          {user ? (
            <div className="flex items-center gap-2">
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarFallback className="text-xs">
                  {(user.email?.[0] || "U").toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-1 flex-col gap-0.5 overflow-hidden">
                <span className="truncate text-xs text-foreground">
                  {user.email}
                </span>
                {accessTier?.paidTier === "pro" && (
                  <Badge variant="secondary" className="w-fit text-[10px]">Pro</Badge>
                )}
                {accessTier?.paidTier === "basic" && (
                  <Badge variant="secondary" className="w-fit text-[10px]">
                    Basic{accessTier.accessEndDate && (() => {
                      const days = Math.ceil((new Date(accessTier.accessEndDate) - new Date()) / 86400000)
                      if (days <= 0) return " (expired)"
                      return ` (${days}d left)`
                    })()}
                  </Badge>
                )}
                {accessTier?.trialAvailable && (
                  <Badge variant="secondary" className="w-fit text-[10px]">Free Trial Available</Badge>
                )}
                {!accessTier?.isPaid && !accessTier?.trialAvailable && accessTier?.tier === "limited" && (
                  <Badge variant="secondary" className="w-fit text-[10px]">Free (Trial Used)</Badge>
                )}
              </div>
              <form action="/auth/signout" method="post">
                <button
                  type="submit"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </form>
            </div>
          ) : (
            <Link
              href="/login"
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <LogIn className="h-3.5 w-3.5" />
              <span>Sign in</span>
            </Link>
          )}
        </SidebarFooter>
      </Sidebar>
      </div>
    </Sidebar>
  )
}
